import { apiClient, toQuery, saveBlob } from "./client";

export async function fetchVisits(jobId) {
  const res = await apiClient("GET", `/visits?job_id=${jobId}`);
  return res.data;
}

// No params → bare array (legacy consumers). With { limit } → the
// { items, total, limit, offset } envelope. Other params: q, status (array),
// since/until (event start window), sort, dir, offset.
export async function fetchAllVisits(params = {}) {
  const query = toQuery(params);
  const res = await apiClient("GET", `/visits${query ? `?${query}` : ""}`);
  return res.data;
}

// `notifyBatch` (a uuid from utils/notifyBatch.js) is passed when SEVERAL visits
// are created/assigned by one action: the server HOLDS the assignment emails so
// they can go out as one grouped email per technician per job, and the caller
// finishes with flushNotifyBatch. Omit it for a single visit — emails go out
// immediately, exactly as before.
const batchQs = (notifyBatch) =>
  notifyBatch ? `?notify_batch=${encodeURIComponent(notifyBatch)}` : "";

export async function createVisit(payload, { notifyBatch } = {}) {
  const res = await apiClient("POST", `/visits${batchQs(notifyBatch)}`, payload);
  return res.data;
}

export async function updateVisit(visitId, payload, { notifyBatch } = {}) {
  const res = await apiClient("PATCH", `/visits/${visitId}${batchQs(notifyBatch)}`, payload);
  return res.data;
}

/** Send the held assignment emails for one batch (grouped per technician per job). */
export async function flushNotifyBatch(batchId) {
  const res = await apiClient("POST", "/notifications/flush-batch", { batch_id: batchId });
  return res.data;
}

export async function fetchVisit(visitId) {
  const res = await apiClient("GET", `/visits/${visitId}`);
  return res.data;
}

export async function deleteVisit(visitId) {
  const res = await apiClient("DELETE", `/visits/${visitId}`);
  return res.data;
}

export async function addVisitNote(visitId, content) {
  const res = await apiClient("POST", `/visits/${visitId}/notes`, { content });
  return res.data;
}

/** Edit an existing visit note's text — management only, any note. */
export async function updateVisitNote(visitId, noteId, content) {
  const res = await apiClient("PATCH", `/visits/${visitId}/notes/${noteId}`, { content });
  return res.data;
}

/**
 * Add a photo to a visit, optionally staged "before" or "after".
 *
 * `stage` is omitted rather than sent empty when unset — the server treats a
 * missing stage as an unstaged photo, and sending "" would fail its validation.
 */
export async function uploadVisitPhoto(visitId, file, stage) {
  const formData = new FormData();
  formData.append("file", file);
  if (stage) formData.append("stage", stage);
  const res = await apiClient("POST", `/visits/${visitId}/photos`, formData);
  return res.data;
}

export async function uploadVisitAttachment(visitId, attachmentType, file) {
  const formData = new FormData();
  formData.append("attachment_type", attachmentType);
  formData.append("file", file);
  const res = await apiClient("POST", `/visits/${visitId}/attachments`, formData);
  return res.data;
}

export async function deleteVisitAttachment(visitId, attachmentId) {
  const res = await apiClient("DELETE", `/visits/${visitId}/attachments/${attachmentId}`);
  return res.data;
}

/**
 * Daily safety sheet for a visit.
 * Returns `{ sheet, defaults, questions, ppe_options }` — the question and PPE
 * catalogs come from the backend so the form can't drift from it.
 */
export async function fetchVisitSafetySheet(visitId) {
  const res = await apiClient("GET", `/visits/${visitId}/safety-sheet`);
  return res.data;
}

// Upsert — submitting a new sheet and editing an existing one both PUT here.
export async function saveVisitSafetySheet(visitId, payload) {
  const res = await apiClient("PUT", `/visits/${visitId}/safety-sheet`, payload);
  return res.data;
}

export async function uploadVisitSafetySignature(visitId, signerName, blob) {
  const formData = new FormData();
  formData.append("signer_name", signerName ?? "");
  formData.append("file", blob, "signature.png");
  const res = await apiClient("POST", `/visits/${visitId}/safety-sheet/signature`, formData);
  return res.data;
}

// Downloads the visit's submitted safety sheet as a printable PDF. 404s (via
// the caller's own try/catch) if nothing has been submitted yet.
export async function downloadVisitSafetySheet(visitId, filenameHint) {
  const res = await apiClient("GET", `/visits/${visitId}/safety-sheet/pdf`, undefined, {
    responseType: "blob",
  });
  saveBlob(res.data, `safety-sheet-${filenameHint || visitId}.pdf`, "application/pdf");
}

/**
 * Job Safety Analysis — the alternate safety-document flavor a job type can
 * pick (see job_types.safety_document_type). Same three-endpoint shape:
 * fetch returns the saved JSA (or null) plus prefill defaults and the
 * hazard/hazard-control/PPE catalogs the form renders from.
 */
export async function fetchVisitJSA(visitId) {
  const res = await apiClient("GET", `/visits/${visitId}/jsa`);
  return res.data;
}

export async function saveVisitJSA(visitId, payload) {
  const res = await apiClient("PUT", `/visits/${visitId}/jsa`, payload);
  return res.data;
}

export async function uploadVisitJSASignature(visitId, signerName, blob) {
  const formData = new FormData();
  formData.append("signer_name", signerName ?? "");
  formData.append("file", blob, "signature.png");
  const res = await apiClient("POST", `/visits/${visitId}/jsa/signature`, formData);
  return res.data;
}

/**
 * The visit the CALLING user is currently clocked in on, or null.
 *
 * Backs the app-wide "clocked in" banner, which renders on every page: the
 * `clock_entries` list that rides along with a visit only helps a surface that
 * already fetched that visit, and no other page has.
 */
export async function fetchMyOpenClock() {
  const res = await apiClient("GET", "/visits/my-open-clock");
  return res.data;
}

/**
 * Clock the *calling* technician in/out on a visit — per-technician, so one
 * technician clocking in never clocks in the rest of the crew. Both return the
 * full updated visit (including its `clock_entries`).
 * Clock-out 400s when the visit's type requires a safety sheet and none is on
 * file, or when its checklist is unfinished.
 */
export async function clockInVisit(visitId, location) {
  // `location` ({lat, lng} or null) is a best-effort snapshot — see utils/geolocation.js.
  const res = await apiClient("POST", `/visits/${visitId}/clock-in`, location || undefined);
  return res.data;
}

/**
 * `note` is optional and about the PUNCH itself ("left early, truck trouble"),
 * distinct from a general visit note — it only ever shows on the Time Card,
 * attributed to the clocking-out technician. See visits_service.clock_out_visit.
 */
/**
 * The CALLER's own last-day Technician Closure tasks blocking clock-out from
 * this visit — `{last_work_day, tech_closure_items: [{id, description}]}`.
 * Only ever non-empty on the job's last work day. Per-viewer, so it is its
 * own endpoint rather than a field on the shared visit payload.
 */
export async function fetchVisitClockOutBlockers(visitId) {
  const res = await apiClient("GET", `/visits/${visitId}/clock-out-blockers`);
  return res.data;
}

export async function clockOutVisit(visitId, note, location) {
  const body = { ...(note ? { note } : {}), ...(location || {}) };
  const res = await apiClient(
    "POST",
    `/visits/${visitId}/clock-out`,
    Object.keys(body).length ? body : undefined
  );
  return res.data;
}

/**
 * Approve (or undo the approval of) one technician's hours on a visit — the
 * office-side counterpart of the punch, gated on `time_entries.approve`
 * (management + system_admin). Approving 400s while that technician is still
 * clocked in. Both return the full updated visit, like clock in/out.
 */
export async function approveVisitClockEntry(visitId, entryId) {
  const res = await apiClient("POST", `/visits/${visitId}/clock-entries/${entryId}/approve`);
  return res.data;
}

export async function unapproveVisitClockEntry(visitId, entryId) {
  const res = await apiClient("POST", `/visits/${visitId}/clock-entries/${entryId}/unapprove`);
  return res.data;
}

/**
 * A technician's own RETROACTIVE Time Card note for this visit — the same
 * idea as `clockOutVisit`'s optional note, except it works any time and
 * whether or not they ever clocked in/out at all. Self-service: the server
 * only accepts it from the primary technician or an assigned crew member.
 * Returns just the created note (`{id, text, author_name, created_at}`), not
 * the whole visit — callers append it locally rather than re-fetching.
 */
export async function addTimeCardNote(visitId, text) {
  const res = await apiClient("POST", `/visits/${visitId}/time-card-note`, { text });
  return res.data;
}

export async function deleteVisitPhoto(visitId, photoId) {
  const res = await apiClient("DELETE", `/visits/${visitId}/photos/${photoId}`);
  return res.data;
}

// Downloads a zip of the selected photos from this visit's own photo list.
export async function downloadVisitPhotosZip(visitId, photoIds) {
  const qs = photoIds.map((id) => `photo_ids=${id}`).join("&");
  const res = await apiClient("GET", `/visits/${visitId}/photos/download?${qs}`, undefined, {
    responseType: "blob",
  });
  saveBlob(res.data, `visit-${visitId}-photos.zip`, "application/zip");
}

/**
 * Every distinct kind of work each person has been on — customer, job type,
 * visit type, PM and location, as COMBINATIONS (one row per distinct visit
 * shape, not per-field sets). Backs the dispatch grid's Availability view,
 * where the visit-shaped filters narrow which technicians show to people who
 * have done that kind of work before. Management only (403 otherwise).
 *
 * → `{ technicians: { [userId]: [{ customer_name, job_type, visit_type_name,
 *      project_manager_name, costco_site_country, property_city }] } }`
 */
export async function fetchTechnicianWorkHistory() {
  const res = await apiClient("GET", "/visits/technician-history");
  return res.data;
}
